<script setup lang="ts">
import { computed } from 'vue';

import { useFormatDate } from '@modules/core/composables/datetime';
import { useI18n } from '@modules/i18n/composables';
import { TRANSLATION_GROUP_ADMIN_PAYMENT_SYSTEM_DETAILS_PENDING_REQUESTS } from '@modules/payment-system-details/constants';
import { EPaymentSystemStatus } from '@modules/payment-system-details/types';
import { isFunction, isString } from '@utils/guards';

import type { IColumnsForDisplayOptions, IColumnsForDisplayResult, IReviewPaymentSystemInfoProps } from './types';
import VContactsMenuButton from '../VContactsMenuButton/VContactsMenuButton.vue';

const { data, isReviewed, localeKey } = defineProps<IReviewPaymentSystemInfoProps>();

const { formatDate } = useFormatDate();
const { getTranslation } = useI18n();

const columnsToDisplay = computed<IColumnsForDisplayOptions[]>(() => [
  { name: 'id' },
  { name: 'affiliate_id' },
  { name: 'affiliate_name' },
  { name: 'manager_id' },
  { name: 'manager_name' },
  {
    name: 'editor_id',
    hide: isReviewed,
  },
  {
    name: 'editor_name',
    hide: isReviewed,
  },
  {
    name: 'created_at',
    hide: isReviewed,
    transform: (value) => isString(value) ? formatDate(value) : value,
  },
  {
    name: 'updated_at',
    hide: !isReviewed,
    transform: (value) => isString(value) ? formatDate(value) : value,
  },
  {
    name: 'status',
    customClass(value) {
      const tag = 'p-tag';

      switch (value) {
        case EPaymentSystemStatus.APPROVED:
          return `${tag} p-tag-success`;
        case EPaymentSystemStatus.REJECTED:
          return `${tag} p-tag-danger`;
        case EPaymentSystemStatus.PENDING:
          return `${tag} p-tag-warn`;
        default:
          return '';
      }
    },
  },
  {
    name: 'rejection_reason',
    label: getTranslation(TRANSLATION_GROUP_ADMIN_PAYMENT_SYSTEM_DETAILS_PENDING_REQUESTS, 'REVIEW_REJECTION_REASON').value,
  },
]);

const columnsToDisplayList = computed<IColumnsForDisplayResult[]>(() => {
  const result: IColumnsForDisplayResult[] = [];

  for (const item of columnsToDisplay.value) {
    if (data?.[item.name] && !item.hide) {
      result.push({
        name: item.name,
        label: item.label,
        value: item.transform ? item.transform(data?.[item.name]) : data?.[item.name],
        customClass: isFunction(item.customClass) ? item.customClass(data?.[item.name]) : item.customClass,
      });
    }
  }

  return result;
});
</script>

<template>
  <div v-data-qa-auto-id="'VReviewPaymentSystemInfo'">
    <div class="flex flex-col gap-1">
      <div
        v-for="item in columnsToDisplayList"
        :key="item.name"
        class="flex gap-2"
      >
        <span class="min-w-[150px] text-muted-color">{{ item.label || $t(`${localeKey}.${item.name}`, { defaultValue: item.name }) }}</span>
        <span :class="item.customClass">{{ item.value }}</span>
      </div>
    </div>
    <div
      v-if="!isReviewed"
      class="mt-3"
    >
      <VContactsMenuButton
        :translate-key="TRANSLATION_GROUP_ADMIN_PAYMENT_SYSTEM_DETAILS_PENDING_REQUESTS"
        :contacts="data?.affiliate_contacts"
        :button-title="$getTranslation(TRANSLATION_GROUP_ADMIN_PAYMENT_SYSTEM_DETAILS_PENDING_REQUESTS, 'CONTACTS_BUTTON')"
      />
    </div>
  </div>
</template>
